<template>
  <div
    class="flex min-h-0 w-full flex-col justify-center py-6 text-gray-900 dark:text-gray-100"
    style="font-family: 'Inter', sans-serif; min-height: calc(100vh - 140px);"
  >
    <!-- product found -->
    <div
      v-if="product"
      class="w-full overflow-hidden rounded-xl border border-dashed border-gray-300/80 bg-gray-50 dark:border-white/15 dark:bg-white/[0.02]"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2">

        <!-- ── LEFT ── -->
        <div class="flex flex-col gap-10 px-8 py-9 sm:px-10 sm:py-10 lg:px-12 lg:py-11">

          <!-- badge -->
          <div>
            <span
              class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-500 dark:border-white/10 dark:bg-white/5 dark:text-gray-400"
            >
              MetalNx: {{ product.name }} Overview
            </span>

            <!-- icon + name -->
            <div class="mt-7 flex items-start gap-5">
              <div
                class="product-icon-box flex shrink-0 flex-none items-center justify-center rounded-md border border-gray-200 bg-white text-[color:var(--v-primary-base)] shadow-sm dark:border-white/10 dark:bg-white/5 dark:shadow-none"
              >
                <icon :id="product.icon" size="1.875rem" />
              </div>

              <div class="min-w-0 flex-1 pt-1">
                <p class="truncate text-xs mb-2 font-semibold uppercase tracking-widest text-[color:var(--v-primary-base)]">
                  {{ product.subtitle }}
                </p>
                  <h1 class="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                  {{ product.nameRaw }}<sup class="text-sm font-normal">™</sup>
                </h1>
              </div>
            </div>

            <!-- description -->
            <p class="mt-7 text-sm leading-7 text-gray-600 dark:text-gray-300">
              {{ product.description }}
            </p>
          </div>

          <!-- CTA -->
          <div
            class="rounded-xl border border-gray-200 bg-gray-50 px-6 py-5 dark:border-white/10 dark:bg-white/[0.03]"
          >
            <p class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Get in touch
            </p>
            <p class="mt-3 text-sm leading-7 text-gray-600 dark:text-gray-300">
              If you'd like to learn more about the {{ product.name }} platform, please contact the MetalNx team for further information.
            </p>
            <div class="mt-5 flex justify-end">
              <c-tailwind-btn
                tag="a"
                variant="primary"
                :href="mailtoLink"
                class="px-7 py-3 text-sm font-semibold shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                Get in touch
              </c-tailwind-btn>
            </div>
          </div>

        </div>

        <!-- ── RIGHT ── -->
        <div
          class="border-t border-gray-200 bg-gray-50/80 px-8 py-9 dark:border-white/10 dark:bg-white/[0.03] sm:px-10 sm:py-10 lg:border-l lg:border-t-0 lg:px-10 lg:py-11"
        >
          <p class="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Key capabilities
          </p>

          <ul class="cap-list mt-6">
            <li
              v-for="cap in product.capabilities"
              :key="cap.text"
              class="cap-item"
            >
              <span class="cap-dot" />
              <span class="cap-text">
                <strong v-if="cap.label" class="cap-label">{{ cap.label }}:&nbsp;</strong>{{ cap.text }}
              </span>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- fallback -->
    <div
      v-else
      class="flex min-h-64 items-center justify-center rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5"
    >
      <p class="text-sm text-gray-500 dark:text-gray-400">Module information not found.</p>
    </div>

  </div>
</template>

<script>
import Icon from '@/components/Icon.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'

const PRODUCTS = {
  pronx: {
    icon: 'menu-ipro',
    name: 'ProNx™',
    nameRaw: 'ProNx',
    subtitle: 'PROCESS PARAMETER OPTIMISATION',
    description:
      'ProNx™ focuses on predicting material behaviour at specific locations within metal components and optimising manufacturing processes.',
    capabilities: [
      { label: 'Microstructure & property prediction', text: 'Prediction of microstructure and mechanical/physical properties at different locations within metal components.' },
      { label: 'Process parameter optimisation', text: 'Optimisation of manufacturing process parameters.' },
      { label: 'Multi-process support', text: 'Support for multiple processes, including casting, rolling, forging, extrusion, powder metallurgy, and additive manufacturing.' },
      { label: 'AI/ML-driven algorithms', text: 'Use of AI/ML-driven predictive algorithms to enhance process efficiency and new product development.' },
    ],
  },
  enginex: {
    icon: 'menu-ai-model-development',
    name: 'EngineX™',
    nameRaw: 'EngineX',
    subtitle: 'High-Throughput Thermodynamic Screening',
    description:
      'EngineX™ is a scalable digital platform that enables rapid, multi-step, manufacturing-informed prediction of microstructure, performance, and processability across vast alloy design spaces.',
    capabilities: [
      { label: 'Multi-step prediction', text: 'High-throughput, multi-step prediction of microstructural constituents, phase evolution, and processability across millions of alloy compositions.' },
      { label: 'Alloy screening', text: 'Rapid alloy screening across user-defined composition ranges at any resolution.' },
      { label: 'Phase diagram computation', text: 'Accelerated computation of phase diagram characteristics for millions of alloy systems within days.' },
      { label: 'Distributed architecture', text: 'Distributed, parallel architecture with multi-cloud deployment for scalable, large-scale computation and workload management.' },
    ],
  },
  heatnx: {
    icon: 'menu-iheat',
    name: 'HeatNx™',
    nameRaw: 'HeatNx',
    subtitle: 'Heat Treatment Optimisation & Development Toolkit',
    description:
      'HeatNx™ combines physical and metallurgical principles with artificial intelligence and machine learning to optimise thermal processing conditions.',
    capabilities: [
      { label: 'Process prediction & optimisation', text: 'Prediction and optimisation of heat treatment processes, including homogenisation, ageing, and hot isostatic pressing (HIP).' },
      { label: 'Thermal cycle optimisation', text: 'Optimisation of time, temperature, and thermal cycles for enhanced process efficiency and material performance.' },
      { label: 'Thermal cycle control', text: 'Improved control of thermal cycles to achieve desired material properties.' },
      { label: 'Data-driven development', text: 'Data-driven process development for enhanced consistency and performance.' },
    ],
  },
  algonx: {
    icon: 'menu-iboost',
    name: 'AlgoNx™',
    nameRaw: 'AlgoNx',
    subtitle: 'Digital Model Development Toolkit',
    description:
      'AlgoNx™ enables the rapid development of robust predictive models and algorithms using advanced data science and artificial intelligence techniques.',
    capabilities: [
      { label: 'Data preparation and visualisation', text: 'Cleaning, visualisation, and outlier detection using domain-informed data science methods.' },
      { label: 'Model development', text: 'Access to over 40 machine learning and AI algorithms, with automated tuning and validation (including train–test split, k-fold cross-validation, and time-based validation).' },
      { label: 'Prediction', text: 'Deployment of trained models/algorithms to generate accurate predictions within defined design spaces.' },
    ],
  },
  targetnx: {
    icon: 'menu-ipredix',
    name: 'TargetX™',
    nameRaw: 'TargetNx',
    subtitle: 'Performance-Driven Alloy Selection',
    description:
      'TargetX™ enables rapid identification of optimal alloy compositions based on required performance criteria. Acting as the inverse of PrediX™, the platform translates target material properties into viable alloy solutions.',
    capabilities: [
      { label: 'Performance input', text: 'Input of required physical and mechanical performance parameters, either individually or via intuitive slider-based controls.' },
      { label: 'Candidate alloy generation', text: 'Rapid generation of candidate alloy compositions that meet defined performance targets.' },
      { label: 'Alloy exploration', text: 'Exploration of multiple alloy options to support informed material selection.' },
      { label: 'Accelerated decision-making', text: 'Linking performance requirements directly to composition design for faster, smarter material selection.' },
    ],
  },
  meltnx: {
    icon: 'menu-imelt',
    name: 'MeltNx™',
    nameRaw: 'MeltNx',
    subtitle: 'Furnace Batching & Casting Charge',
    description:
      'MeltNx™ provides intelligent tools for managing material inputs and designing optimal casting charges.',
    capabilities: [
      { label: 'Inventory & charge design', text: 'Materials inventory management and charge design.' },
      { label: 'Charge optimisation', text: 'Optimisation of casting charges based on cost, carbon footprint, stock availability, or combined objectives.' },
      { label: 'Sustainable melting', text: 'Enhanced decision-making for efficient and sustainable melting operations.' },
    ],
  },
  alloynx: {
    icon: 'menu-imat',
    name: 'AlloyNx™',
    nameRaw: 'AlloyNx',
    subtitle: 'Digital Alloy Design & Optimisation Toolkit',
    description:
      'AlloyNx™ enables rapid design and optimisation of advanced alloys through predictive modelling and intuitive visual analytics.',
    capabilities: [
      { label: 'Alloy performance prediction', text: 'Prediction of microstructure, processability, and performance across millions of potential alloy compositions.' },
      { label: 'Design space visualisation', text: 'Visualisation of large design spaces using intuitive graphical tools.' },
      { label: 'Property trade-offs', text: 'Exploration of trade-offs between multiple predicted material properties.' },
      { label: 'Multi-objective design', text: 'Enablement of multi-objective alloy design and optimisation.' },
      { label: 'Integrated filtering', text: 'Integrated filtering tools to identify optimal alloy solutions based on specific requirements.' },
    ],
  },
  brainnx: {
    icon: 'menu-itrain',
    name: 'BrainNx™',
    nameRaw: 'BrainNx',
    subtitle: 'AI-Powered Intelligent Agent',
    description:
      'BrainNx™ is an advanced artificial intelligence agent that delivers expert guidance across alloys and manufacturing processes through intuitive, real-time interaction.',
    capabilities: [
      { label: 'Conversational AI assistant', text: 'Conversational AI assistant for metallurgical and process-related insights.' },
      { label: 'Alloy design support', text: 'Intelligent support for alloy design, optimisation, and material selection.' },
      { label: 'Heat treatment guidance', text: 'Guidance on heat treatment design and optimisation of thermal processes.' },
      { label: 'Real-time decision support', text: 'Real-time, data-driven decision support for materials, heat treatment and processing strategies.' },
    ],
  },
}

export default {
  name: 'LockedModuleInfo',
  components: { Icon, CTailwindBtn },
  computed: {
    product() {
      const id = (this.$route.params.id || '').toLowerCase()
      return PRODUCTS[id] || null
    },
    mailtoLink() {
      if (!this.product) return 'mailto:info@metalnx.com'
      const subject = encodeURIComponent(`Inquiry About ${this.product.name}`)
      const body = encodeURIComponent(`Hello MetalNx team,\n\nI would like to learn more about the ${this.product.name} platform.\n`)
      return `mailto:info@metalnx.com?subject=${subject}&body=${body}`
    },
  },
  watch: {
    product: {
      immediate: true,
      handler(product) {
        if (product) {
          document.title = `MetalNx: ${product.nameRaw} Overview`
        }
      },
    },
  },
}
</script>

<style scoped>
/*
  Only scoped styles below — purely to reset Vuetify's forced
  ul/li margins and to lay out the capability cards reliably.
  All visual design (colors, radius, borders) uses Tailwind above.
*/

.product-icon-box {
  box-sizing: border-box;
  flex: 0 0 4rem;
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  min-height: 4rem;
  max-width: 4rem;
  max-height: 4rem;
  aspect-ratio: 1 / 1;
  overflow: hidden;
}

.product-icon-box ::v-deep(svg.icon),
.product-icon-box ::v-deep(.icon--mask) {
  display: block;
  flex-shrink: 0;
  width: 1.875rem !important;
  height: 1.875rem !important;
  min-width: 1.875rem !important;
  min-height: 1.875rem !important;
  max-width: 1.875rem !important;
  max-height: 1.875rem !important;
  margin: 0 !important;
}

.cap-list {
  margin: 0 !important;
  padding: 0 !important;
  list-style: none !important;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cap-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  list-style: none !important;
  margin: 0 !important;
}

.theme--dark .cap-item {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  box-shadow: none;
}

.cap-dot {
  display: block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: var(--v-primary-base);
}

.cap-text {
  display: block;
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  line-height: 1.65;
  color: #374151;
}

.theme--dark .cap-text {
  color: #d1d5db;
}

.cap-label {
  font-weight: 600;
  color: #1f2937;
}

.theme--dark .cap-label {
  color: #f9fafb;
}
</style>
